import React, { useContext, useEffect, useState } from "react";
import { ChevronDownIcon, QuestionMarkIcon } from "../utils/miscIcons";
import formatCurrency from "../func/currency_formatter";
import ProductsList from "./productsList";
import DiscountInput from "./inputs/discountInput";
import Separator from "./Separator";
import ExtendedWarrantyCheckbox from "./ExtendedWarrantyCheckbox";
import { TemplateContext } from "../layout";
import TotalSavings from "./orderSummary/TotalSavings";

const orderOverviewDrawerV3 = () => {
  const [templateContext, setTemplateContext] = useContext(TemplateContext);
  const [total, setTotal] = useState(0);
  const [savings, setSavings] = useState(0);
  const summary = window.post_template?.checkout_v4_summary;
  const shippingPolicyUrl = summary.checkout_v4_summary_shippingLink;
  const isDiscountApplied = templateContext.discountCode === window.productData?.discount_codes?.discount_code;

  const getPrice = (basePrice, promoPrice, amount = 1) => {
    const price = isDiscountApplied && promoPrice ? promoPrice : basePrice;
    return Number(price) * amount;
  };

  const shippingPrice = getPrice(
    templateContext.config.product.shipping_price,
    templateContext.config.product.promo_shipping_price
  );

  const productPrice = getPrice(
    templateContext.config.product.price,
    templateContext.config.product.promo_discounted_price,
    templateContext.config.product.amount
  );

  const warrantyAmount = window.productData.multiply_price_by_amount ? templateContext.config.product.amount : 1;

  const warrantyPrice = templateContext.config.warrantyState
    ? templateContext.config.product.warranty_price * warrantyAmount
    : 0;

  const subtotal = productPrice + warrantyPrice;

  useEffect(() => {
    let finalSavings = 0;

    templateContext?.config?.product?.fake_products &&
      templateContext.config.product.fake_products.map((e) => (finalSavings = Number(finalSavings) + Number(e.price)));

    if (isDiscountApplied || !templateContext.discountsEnabled) {
      finalSavings += Number(templateContext.config.product.old_price) - productPrice;
    }

    setSavings(finalSavings.toFixed(2));

    const finalPrice = subtotal + shippingPrice;
    setTotal(finalPrice.toFixed(2));
    setTemplateContext({ ...templateContext, finalPrice });
  }, [subtotal, warrantyPrice, shippingPrice, isDiscountApplied]);

  const formatPrice = (price, withCents = false) => {
    const numberPrice = Number(price);
    if (window.formattedString) {
      return window.formattedString.replace(/(0\.00|0,00|0)/, withCents ? numberPrice.toFixed(2) : numberPrice);
    }
    return formatCurrency(withCents ? numberPrice.toFixed(2) : numberPrice);
  };

  const getFormattedPrices = () => {
    const withCents = templateContext?.priceWithCents;
    return {
      formattedSavings: formatPrice(savings, withCents),
      formattedTotal: formatPrice(total, withCents),
      formattedShippingPrice: formatPrice(shippingPrice, withCents),
      formattedSubtotal: formatPrice(subtotal, withCents),
    };
  };

  const { formattedSavings, formattedTotal, formattedShippingPrice, formattedSubtotal } = getFormattedPrices();

  return (
    <div className="orderOverviewDrawerV3">
      <div
        data-cy="cc-order-summary-container"
        className={`orderOverviewDrawerV3__topCont ${
          templateContext.orderOverviewDrawerOpen && "orderOverviewDrawerV3__topCont__hide"
        }`}
      >
        <div className={`orderOverviewDrawerV3__savingsLabel__cont`}>
          <div className="orderOverviewDrawerV3__savingsLabel">{summary.tr_checkout_v4_summary_savingsLabel}</div>
          <div className="orderOverviewDrawerV3__savingsLabel">{formattedSavings}</div>
        </div>
        <Separator />
      </div>
      <div
        data-cy="cc-order-review-opener"
        className={`orderOverviewDrawerV3__opener ${
          templateContext.orderOverviewDrawerOpen ? "_down" : ""
        } orderOverviewDrawerV3_label`}
        onClick={() => {
          setTemplateContext({
            ...templateContext,
            orderOverviewDrawerOpen: !templateContext.orderOverviewDrawerOpen,
          });
        }}
      >
        <div className="orderOverviewDrawerV3_label_title">
          <span style={{ marginRight: "8px" }}>
            {window.post_template.checkout_v4_summary.tr_checkout_v4_order_review_accordion_title}
          </span>{" "}
          <ChevronDownIcon />
        </div>
        <div className="orderOverviewDrawerV3_label_priceContainer">
          <div className="orderOverviewDrawerV3_label_price">{formattedTotal}</div>
        </div>
      </div>

      <div
        style={templateContext?.displayPopup ? { overflow: "visible" } : {}}
        className={`orderOverviewDrawerV3__content ${
          templateContext.orderOverviewDrawerOpen && "orderOverviewDrawerV3__content__show"
        }`}
      >
        <Separator margin="16px 0px 16px 0px" />
        <ProductsList />
        {window.productData?.add_warranty_for_product && (
          <>
            <Separator margin="16px 0px 16px 0px" />
            <ExtendedWarrantyCheckbox />
          </>
        )}
        <Separator margin="16px 0px 0px 0px" />
        {templateContext.discountsEnabled && (
          <>
            <DiscountInput /> <Separator margin="0px 0px 0px 0px" />
          </>
        )}
        <div className="orderTotals__container" style={{ marginBottom: "0px" }}>
          <div className="orderTotals">
            <div className="orderTotals__item">
              <p>{window.post_template?.checkout_v4_summary.tr_checkout_v4_summary_orderLabel}</p>
              <p data-cy="summary-subtotal">{formattedSubtotal}</p>
            </div>
            <div className="orderTotals__item">
              <p className="orderTotals__item__shippingLabel">
                <span style={{ marginRight: "4px" }}>
                  {window.post_template.checkout_v4_summary.tr_checkout_v4_summary_shippingLabel}
                </span>{" "}
                <a className="orderTotals__item__shippingLabel--link" href={shippingPolicyUrl}>
                  <QuestionMarkIcon />
                </a>
              </p>
              <p data-cy="summary-shipping">{formattedShippingPrice}</p>
            </div>
          </div>
          <Separator />
          <TotalSavings
            type={templateContext?.totalSavingsStyle}
            label={window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_savingsLabel}
            savings={formattedSavings}
          />
          <div className="total">
            <div>{window.post_template?.checkout_v4_summary.tr_checkout_v4_summary_totalLabel}</div>
            <div data-cy="summary-total">{formattedTotal}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default orderOverviewDrawerV3;
